<script lang="ts">
	import { writable, get } from 'svelte/store';
	import ReportsLoading from '../../../../../../../shared/components/common/Loading/ReportsLoading.svelte';
	import LocaleSelector from '../../../../../../../shared/components/common/LocaleSelector.svelte';
	import NoDataAvailable from '../../../../../../../shared/components/common/NoDataAvailable.svelte';
	import SurveyReport from '../../../../../../../shared/components/common/Survey/SurveyReport.svelte';
	import Questionario from '../../../../../../../shared/models/Questionario/Questionario';
	import QuestionarioRisposte from '../../../../../../../shared/models/Questionario/QuestionarioRisposte';
	import { locale, setupI18n } from '../../../../../../../shared/services/i18n';
	import Sede from '../../../../../../../shared/models/Sede/Sede';
	import AziendaEnte from '../../../../../../../shared/models/AziendaEnte/AziendaEnte';
	import Checkbox from '@smui/checkbox';
	import Button, { Label } from '@smui/button';
	import IconButton, { Icon } from '@smui/icon-button';
	import Accordion, { Content, Header, Panel } from '@smui-extra/accordion';
	import FieldLoading from '../../../../../../../shared/components/common/Loading/FieldLoading.svelte';
	import { onMount, onDestroy, tick } from 'svelte';
	import ExpandIcon from '../../../../../../../shared/components/common/Icons/ExpandIcon.svelte';
	import ProgressLoading from '../../../../../../../shared/components/common/Loading/ProgressLoading.svelte';
	
	window.localStorage.removeItem('aziendeRispondentiId');
	window.localStorage.removeItem('questionariAziendeReportId');

	let stringifiedQuestionarioJson = '';
	let stringifiedQuestionarioJsonUpdated = '';
	let risposteJson = [];
	let risposteJsonUpdated = [];
	let showReport = true;
	let accordionOpen = true; 
	
	let aziende = [];
	let loading_aziende = writable(false);
	let expandedAziende = {}; 

	let questionariId: number[] = localStorage.getItem('questionariSediReportId') !== null ? JSON.parse(localStorage.getItem('questionariSediReportId')) : [];
	let sediRispondentiId: number[] = localStorage.getItem('sediRispondentiId') !== null ? JSON.parse(localStorage.getItem('sediRispondentiId')) : [];
	
	let availableSedi = [];
	let loading_sedi = writable(false);
	let sediByAzienda = {}; 
	
	let selectedSediIds = [...sediRispondentiId];

	let [data, loading, error] = Questionario.merge(questionariId);
	
	let isLoading = writable(true);
	
	let currentFilters = {
		questionariId: [...questionariId],
		sediId: [...selectedSediIds],
	};
	
	let subscriptions = [];
	
	function unsubscribeAll() {
		subscriptions.forEach(unsub => {
			if (typeof unsub === 'function') {
				unsub();
			}
		});
		subscriptions = [];
	}
	
	function loadSediAndAziende() {
		loading_sedi.set(true);
		loading_aziende.set(true);
		
		const sediFilter = {
			sediId: sediRispondentiId
		};
		
		const [dataSedi, loadingSedi, errorSedi] = Sede.getList(sediFilter);
		const [dataAziende, loadingAziende, errorAziende] = AziendaEnte.getList();
		
		const sediSubscription = dataSedi.subscribe(value => {
			if (value && value.response) {
				availableSedi = value.response;
				
				sediByAzienda = groupSediByAzienda(availableSedi);
				loading_sedi.set(false);
			}
		});
		
		const aziendeSubscription = dataAziende.subscribe(value => {
			if (value && value.response) {
				aziende = value.response;
				loading_aziende.set(false);
			}
		});
		
		subscriptions.push(sediSubscription, aziendeSubscription);
	}
	
	function groupSediByAzienda(sedi) {
		const grouped = {};
		
		sedi.forEach(sede => {
			if (sede.aziendaEnteId) {
				if (!grouped[sede.aziendaEnteId]) {
					grouped[sede.aziendaEnteId] = [];
				}
				grouped[sede.aziendaEnteId].push(sede);
			}
		});
		
		return grouped;
	}
	
	function getAziendaName(aziendaId) {
		const azienda = aziende.find(a => a.id === aziendaId);
		return azienda ? azienda.nomeAziendaEnte : 'Azienda/Ente sconosciuto';
	}
	
	function toggleAzienda(aziendaId) {
		expandedAziende[aziendaId] = !expandedAziende[aziendaId];
		expandedAziende = {...expandedAziende};
	}
	
	function areAllSediSelected(aziendaId) {
		const sedi = sediByAzienda[aziendaId] || [];
		if (sedi.length === 0) return false;
		
		return sedi.every(sede => selectedSediIds.includes(sede.id));
	}
	
	function areSomeSediSelected(aziendaId) {
		const sedi = sediByAzienda[aziendaId] || [];
		return sedi.some(sede => selectedSediIds.includes(sede.id)) && !areAllSediSelected(aziendaId);
	}
	
	function toggleSelectAllSediOfAzienda(aziendaId, select) {
		const sedi = sediByAzienda[aziendaId] || [];
		
		if (select) {
			const sediIds = sedi.map(sede => sede.id);
			selectedSediIds = [...new Set([...selectedSediIds, ...sediIds])];
		} else {
			selectedSediIds = selectedSediIds.filter(id => !sedi.some(sede => sede.id === id));
		}
	}
	
	function loadAndPrepareReportData() {
		isLoading.set(true);
		
		const [dataRisposte, loadingRisposte, errorRisposte] = QuestionarioRisposte.getList(currentFilters);
		
		const loadingSubscription = loadingRisposte.subscribe(loading => {
			isLoading.set(loading);
		});
		
		const risposteSubscription = dataRisposte.subscribe(async value => {
			if (value && value.response) {
				
				const parsed = value.response
					.filter(risposta => risposta && risposta.questionarioRisposteJson)
					.map(risposta => {
						try {
							return JSON.parse(risposta.questionarioRisposteJson);
						} catch (e) {
							return null;
						}
					})
					.filter(item => item !== null);
				
				risposteJson = parsed;
				
				if (stringifiedQuestionarioJson && parsed.length > 0) {
					prepareReportData(stringifiedQuestionarioJson, parsed);
				} else {
					showReport = false;
					risposteJsonUpdated = [];
					
					await tick();
					
					showReport = true;
					isLoading.set(false);
				}
			} else {
				risposteJson = [];
				risposteJsonUpdated = []; 
				showReport = false;
				await tick();
				showReport = true;
				isLoading.set(false);
			}
		});
		
		subscriptions.push(loadingSubscription, risposteSubscription);
	}
	
	async function prepareReportData(questionarioString, risposteArray) {
		isLoading.set(true);
		
		const [reportData, reportLoading, reportError] = Questionario.prepareSurveyForGraphicReports(
			questionarioString,
			JSON.stringify(risposteArray)
		);
		
		const loadingSubscription = reportLoading.subscribe(loading => {
			isLoading.set(loading);
		});
		
		const dataSubscription = reportData.subscribe(async value => {
			if (value && value.response) {
				
				showReport = false;
				
				if (value.response.questionarioJsonStringified) {
					stringifiedQuestionarioJsonUpdated = value.response.questionarioJsonStringified;
				}
				
				if (value.response.risposte) {
					risposteJsonUpdated = [...value.response.risposte];
				}
				
				await tick();
				
				showReport = true;
			} else {
			}
		});
		
		const errorSubscription = reportError.subscribe(err => {
			if (err) {
				isLoading.set(false);
			}
		});
		
		subscriptions.push(loadingSubscription, dataSubscription, errorSubscription);
	}
	
	function updateSelectedSedi() {
		
		currentFilters = {
			...currentFilters,
			sediId: [...selectedSediIds]
		};
		
		unsubscribeAll();
		
		loadAndPrepareReportData();
	}
	
	function toggleSelectAllSedi(select) {
		if (select) {
			selectedSediIds = availableSedi.map(sede => sede.id);
		} else {
			selectedSediIds = [];
		}
		
		selectedSediIds = [...selectedSediIds];
	}
	
	const questionarioSubscription = data.subscribe(value => {
		if (value) {
			stringifiedQuestionarioJson = value;
			
			if (!get(isLoading)) {
				loadAndPrepareReportData();
			}
		}
	});
	
	subscriptions.push(questionarioSubscription);
	
	onMount(() => {
		loadSediAndAziende();
		
		if (stringifiedQuestionarioJson) {
			loadAndPrepareReportData();
		}
	});
	
	onDestroy(() => {
		unsubscribeAll();
	});
</script>

<Accordion>
	<Panel square variant="outlined" color="primary" extend bind:open={accordionOpen}>
		<Header>
			Filtra sedi per report
			<IconButton slot="icon" toggle pressed={accordionOpen}>
				<Icon class="material-icons" on>unfold_less</Icon>
				<Icon class="material-icons">unfold_more</Icon>
			</IconButton>
		</Header>
		<Content>
			{#if $loading_sedi || $loading_aziende}
				<FieldLoading />
			{:else if Object.keys(sediByAzienda).length === 0}
				<p>Nessuna sede disponibile</p>
			{:else}
				<div class="flex justify-end mb-4">
					<Button
						on:click="{updateSelectedSedi}"
						type="button"
						variant="raised"
						class="w-auto ml-4"
					>
						<Label class="text-sm">Aggiorna report</Label>
					</Button>
				</div>
				
				<div class="max-h-96 overflow-auto">
					<table class="min-w-full p-0 m-0 text-sm leading-10 border-t-4 border-collapse border-black table-auto">
						<thead>
							<tr class="p-1">
								<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"></th>
								<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Azienda/Ente</th>
								<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Sedi disponibili</th>
								<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">
									<Checkbox 
										checked={availableSedi.length > 0 && selectedSediIds.length === availableSedi.length}
										indeterminate={selectedSediIds.length > 0 && selectedSediIds.length < availableSedi.length}
										on:change={(e) => toggleSelectAllSedi(e.target.checked)}
									/>
								</th>
							</tr>
						</thead>
						<tbody>
							{#each Object.keys(sediByAzienda) as aziendaId}
								{@const aziendaSedi = sediByAzienda[aziendaId] || []}
								<tr class="odd:bg-white even:bg-gray-50 hover:bg-gray-50 p-1">
									<td class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">
										<ExpandIcon 
											condition={expandedAziende[aziendaId]} 
											onClick={() => toggleAzienda(aziendaId)}
										/>
									</td>
									<td class="border-b p-2.5 text-left leading-5">{getAziendaName(Number(aziendaId))}</td>
									<td class="border-b p-2.5 text-center leading-5">{aziendaSedi.length}</td>
									<td class="border-b p-2.5 text-center leading-5">
										<Checkbox 
											checked={areAllSediSelected(aziendaId)}
											indeterminate={areSomeSediSelected(aziendaId)}
											on:change={(e) => toggleSelectAllSediOfAzienda(aziendaId, e.target.checked)}
										/>
									</td>
								</tr>
								
								{#if expandedAziende[aziendaId]}
									<tr>
										<td colspan="4" class="p-0 border">
											<div class="max-h-64 overflow-auto">
												<table class="min-w-full p-0 m-0 text-sm leading-10 table-auto">
													<thead>
														<tr class="p-1 bg-gray-100">
															<th scope="col" class="pl-8 text-xs leading-6 tracking-wider text-left border">Denominazione</th>
															<th scope="col" class="text-xs leading-6 tracking-wider text-center border">Localizzazione</th>
															<th scope="col" class="text-xs leading-6 tracking-wider text-center border">Indirizzo</th>
														</tr>
													</thead>
													<tbody>
														{#each aziendaSedi as sede}
															<tr class="odd:bg-white even:bg-gray-50">
																<td class="pl-8 leading-5 text-left border">{sede.denominazione ?? ''}</td>
																<td class="leading-5 text-center border">{sede.localizzazione ?? ''}</td>
																<td class="leading-5 text-center border">
																	{sede.indirizzo ?? ''}
																	{#if sede.provincia && sede.citta && sede.cap && sede.indirizzoOK}
																		<span
																			title="Indirizzo valido"
																			class="material-icons-outlined"
																			style="color: green; font-size: 20px;">fmd_good</span
																		>
																	{:else}
																		<span
																			title="Indirizzo non valido"
																			class="material-icons-outlined"
																			style="color: red; font-size: 20px;">wrong_location</span
																		>
																	{/if}
																</td>
																<td class="leading-5 text-center border">
																	<Checkbox
																		checked={selectedSediIds.includes(sede.id)}
																		on:change={(e) => {
																			if (e.target.checked) {
																				selectedSediIds = [...selectedSediIds, sede.id];
																			} else {
																				selectedSediIds = selectedSediIds.filter(id => id !== sede.id);
																			}
																		}}
																	/>
																</td>
															</tr>
														{/each}
													</tbody>
												</table>
											</div>
										</td>
									</tr>
								{/if}
							{/each}
						</tbody>
					</table>
				</div>
				
				<div class="flex items-center justify-between mt-4">
					<span class="text-sm text-gray-600">
						{selectedSediIds.length} sedi selezionate su {availableSedi.length} disponibili
					</span>
				</div>
			{/if}
		</Content>
	</Panel>
</Accordion>

{#if $isLoading || $loading}
		<!--<ReportsLoading />-->
	<ProgressLoading />
{:else if showReport && stringifiedQuestionarioJsonUpdated && risposteJsonUpdated?.length > 0}
	<div class="text-end pb-4">
		<LocaleSelector value="{$locale}" on:locale-changed="{(e) => setupI18n({ withLocale: e.detail })}" />
	</div>
	<SurveyReport 
		stringifiedQuestionarioJson={stringifiedQuestionarioJsonUpdated} 
		risposteJson={risposteJsonUpdated} 
	/>
{:else}
	<div class="p-4">
		<NoDataAvailable />
	</div>
{/if}